import * as React from "react";
import { Loader2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  useToast,
} from "@ando/ui";
import {
  listAdminOrgMemberConnectors,
  setAdminOrgMemberConnector,
  updateAdminOrgMemberProductAccess,
  type AdminProductAccess,
  type AdminMemberConnectorAccess,
  type AdminOrganization,
  type AdminOrganizationMember,
  type AdminUpdateMemberLimitsRequest,
} from "@ando/core";

import { adminOrgKeys, useUpdateAdminOrgMemberLimits } from "@/features/admin/hooks/useAdminOrg";

import { tierLabel } from "./_shared";

/**
 * MemberSettingsDialog — per-member overrides for tier + connector access.
 *
 * v1 reference: inline dialog inside
 * ``ando-frontend/.../OrganizationDetailPage.tsx`` (search ``settingsMember``).
 * Two scoped sections:
 *
 * 1. **Limits override** — `usage_multiplier_override` (number from a curated
 *    preset list with an "inherit" option), optional `cost_cap_eur` and
 *    `storage_limit_bytes` numeric overrides. Saved via
 *    ``updateAdminOrgMemberLimits`` (single PUT, partial body — only fields
 *    the admin touched are sent).
 *
 * 2. **Connector access** — per-member ON/OFF for every connector the org
 *    has licensed in ``per_user`` mode. ``listAdminOrgMemberConnectors``
 *    already returns ``can_toggle`` per row (false for `all_members` mode
 *    licenses → switch is disabled with an explanatory hint). Toggles
 *    write through ``setAdminOrgMemberConnector`` and invalidate the
 *    per-member list AND the org-wide ``members`` list so the parent
 *    table reflects the new effective state on close.
 */

export interface MemberSettingsDialogProps {
  org: AdminOrganization;
  member: AdminOrganizationMember | null;
  onOpenChange: (open: boolean) => void;
}

// Preset multiplier set rendered in the dropdown — derived from the
// canonical ``tierLabel`` map in ``_shared.tsx`` so adding a new tier
// is a one-line change there. Order matters (UI shows them ascending).
const TIER_PRESETS: ReadonlyArray<{ multiplier: number; label: string }> = [
  1, 3, 5, 10, 20, 50,
].map((m) => ({ multiplier: m, label: tierLabel(m) }));

/**
 * Build the multiplier dropdown options. Always include the org-default
 * preset list; if the member's current override is a legacy value not in
 * the preset list, prepend a synthetic ``(legacy)`` row so the dropdown
 * accurately reflects the persisted state instead of falling back to
 * "inherit" on first open.
 */
function buildMultiplierOptions(
  currentOverride: number | null,
): Array<{ multiplier: number; label: string }> {
  if (currentOverride == null) return [...TIER_PRESETS];
  if (TIER_PRESETS.some((t) => t.multiplier === currentOverride)) {
    return [...TIER_PRESETS];
  }
  return [
    { multiplier: currentOverride, label: `${tierLabel(currentOverride)} (legacy)` },
    ...TIER_PRESETS,
  ];
}

/**
 * Empty-input → ``null`` (inherit), non-empty → finite number; reject
 * NaN / negatives by snapping back to ``null``.
 */
function parseNumericOverride(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

export function MemberSettingsDialog({
  org,
  member,
  onOpenChange,
}: MemberSettingsDialogProps): React.ReactElement {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const qc = useQueryClient();
  const updateLimits = useUpdateAdminOrgMemberLimits(org.id);

  // Connector list lives in its own query key so toggles don't bust the
  // entire members table. Enabled only while a member is open.
  const connectorsQuery = useQuery({
    queryKey: ["admin", "org-member-connectors", org.id, member?.user_id ?? null] as const,
    queryFn: () =>
      member
        ? listAdminOrgMemberConnectors(org.id, member.user_id)
        : Promise.resolve<AdminMemberConnectorAccess[]>([]),
    enabled: member !== null,
    staleTime: 15_000,
  });

  // Local form state — initialized from ``member`` on open and kept
  // independent of the source query so the user can edit freely without
  // any "pending invalidation" jank.
  const [multiplier, setMultiplier] = React.useState<string>("inherit");
  const [costCap, setCostCap] = React.useState<string>("");
  const [storageGb, setStorageGb] = React.useState<string>("");
  const [savingLimits, setSavingLimits] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("access");
  const [productAccess, setProductAccess] = React.useState<AdminProductAccess | "">("");
  const [savingAccess, setSavingAccess] = React.useState(false);
  const [pendingConnector, setPendingConnector] = React.useState<string | null>(null);

  // Reset form when a new member is opened. Using ``member.user_id`` as
  // the dep key prevents re-resetting if the parent re-renders without
  // swapping members (e.g. toast state change). ``effective_storage_gb``
  // / ``effective_cost_cap_eur`` are server-computed effective values,
  // NOT the raw override — so we leave the override inputs blank to mean
  // "inherit" until the admin explicitly types a number.
  React.useEffect(() => {
    if (!member) return;
    setActiveTab("access");
    setMultiplier(
      member.usage_multiplier_override != null
        ? String(member.usage_multiplier_override)
        : "inherit",
    );
    setCostCap("");
    setStorageGb("");
    setProductAccess(member.product_access ?? "");
  }, [member]);

  const handleSaveLimits = React.useCallback((): void => {
    if (!member) return;
    const body: AdminUpdateMemberLimitsRequest = {
      usage_multiplier_override: multiplier === "inherit" ? null : Number(multiplier),
    };
    const costCapTrim = costCap.trim();
    if (costCapTrim !== "") {
      body.cost_cap_eur = parseNumericOverride(costCapTrim);
    }
    const storageTrim = storageGb.trim();
    if (storageTrim !== "") {
      const gb = parseNumericOverride(storageTrim);
      body.storage_limit_bytes = gb == null ? null : Math.round(gb * 1024 * 1024 * 1024);
    }
    setSavingLimits(true);
    updateLimits.mutate(
      { userId: member.user_id, body },
      {
        onSuccess: () => {
          toast.success(i18n._(msg`Impostazioni salvate`), {
            description: i18n._(msg`Modifiche applicate al membro.`),
          });
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(i18n._(msg`Salvataggio fallito`), {
            description: err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
          });
        },
        onSettled: () => setSavingLimits(false),
      },
    );
  }, [member, multiplier, costCap, storageGb, updateLimits, toast, onOpenChange, i18n]);

  const handleToggleConnector = React.useCallback(
    (connectorKey: string, nextAssigned: boolean): void => {
      if (!member) return;
      setPendingConnector(connectorKey);
      setAdminOrgMemberConnector(org.id, member.user_id, connectorKey, nextAssigned)
        .then(() => {
          // Invalidate BOTH the per-member connector list and the
          // org-wide members list so the parent OrgMembersTab can pick
          // up effective changes (e.g. a tier badge dependent on
          // assignment count, if added later).
          qc.invalidateQueries({
            queryKey: ["admin", "org-member-connectors", org.id, member.user_id],
          });
          qc.invalidateQueries({
            queryKey: adminOrgKeys.members(org.id),
          });
        })
        .catch((err: unknown) => {
          toast.error(i18n._(msg`Accesso connector`), {
            description: err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
          });
        })
        .finally(() => setPendingConnector(null));
    },
    [member, org.id, qc, toast, i18n],
  );

  const open = member !== null;

  // The dialog renders nothing functional when ``member`` is null, but
  // Radix Dialog requires a stable DOM tree across open/close transitions
  // so we keep the component mounted and gate ``open`` on the prop.
  const multiplierOptions = React.useMemo(
    () => buildMultiplierOptions(member?.usage_multiplier_override ?? null),
    [member?.usage_multiplier_override],
  );

  const orgTierLabel = `${tierLabel(org.usage_multiplier)} ×${org.usage_multiplier}`;
  const connectors = connectorsQuery.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ariaTitle={i18n._(msg`Impostazioni membro`)} className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{i18n._(msg`Impostazioni membro`)}</DialogTitle>
          <DialogDescription>
            {i18n._(
              msg`Tier usage e accesso connector per ${member?.name ?? member?.email ?? i18n._(msg`questo membro`)}.`,
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="access">{i18n._(msg`Accesso`)}</TabsTrigger>
            <TabsTrigger value="limits">{i18n._(msg`Limiti`)}</TabsTrigger>
            <TabsTrigger value="connectors">{i18n._(msg`Connector`)}</TabsTrigger>
          </TabsList>

          <TabsContent value="access" className="space-y-4 pt-3">
            <label htmlFor="member-product-access" className="block font-medium text-sm text-ink-2">
              {i18n._(msg`Prodotti disponibili`)}
            </label>
            <Select
              value={productAccess}
              onValueChange={(value) => setProductAccess(value as AdminProductAccess)}
              disabled={savingAccess}
            >
              <SelectTrigger id="member-product-access" ariaLabel={i18n._(msg`Prodotti disponibili`)} placeholder={i18n._(msg`Scegli l'accesso`)} size="lg" />
              <SelectContent>
                <SelectItem value="workspace">{i18n._(msg`Solo Workspace`)}</SelectItem>
                <SelectItem value="engine">{i18n._(msg`Solo Engine`)}</SelectItem>
                <SelectItem value="both">{i18n._(msg`Workspace ed Engine`)}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm leading-relaxed text-ink-3">
              {i18n._(
                msg`La scelta è indipendente dal ruolo. Engine comprende Console e API; Workspace mantiene il proprio stato di attivazione.`,
              )}
            </p>
            <Button
              disabled={
                !member || !productAccess || savingAccess || productAccess === member.product_access
              }
              onClick={async () => {
                if (!member || !productAccess) return;
                setSavingAccess(true);
                try {
                  await updateAdminOrgMemberProductAccess(org.id, member.user_id, productAccess);
                  await qc.invalidateQueries({ queryKey: adminOrgKeys.members(org.id) });
                  toast.success(i18n._(msg`Accesso aggiornato`));
                  onOpenChange(false);
                } catch (error) {
                  toast.error(i18n._(msg`Operazione fallita`), {
                    description: error instanceof Error ? error.message : undefined,
                  });
                } finally {
                  setSavingAccess(false);
                }
              }}
            >
              {savingAccess && <Loader2 aria-hidden className="size-4 animate-spin" />}
              {i18n._(msg`Salva accesso`)}
            </Button>
          </TabsContent>

          <TabsContent value="limits" className="space-y-4 pt-2">
            <div className="space-y-2">
              <label
                htmlFor="member-tier-select"
                className="block font-medium text-[12px] text-ink-2"
              >
                {i18n._(msg`Tier usage`)}
              </label>
              <Select
                value={multiplier}
                onValueChange={setMultiplier}
              >
                <SelectTrigger id="member-tier-select" ariaLabel={i18n._(msg`Tier usage`)} size="lg" />
                <SelectContent>
                <SelectItem value="inherit">{i18n._(msg`Standard (eredita — ${orgTierLabel})`)}</SelectItem>
                {multiplierOptions.map((t) => (
                  <SelectItem key={t.multiplier} value={String(t.multiplier)}>
                    {t.label} (×{t.multiplier})
                  </SelectItem>
                ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-ink-3">
                {i18n._(
                  msg`Effettivo: storage ${member?.effective_storage_gb ?? "—"} GB · cost cap €${member?.effective_cost_cap_eur ?? "—"}/mese.`,
                )}
              </p>
            </div>

            <Separator />

            <div className="space-y-2">
              <label htmlFor="member-cost-cap" className="block font-medium text-[12px] text-ink-2">
                {i18n._(msg`Cost cap (€/mese)`)}
                <span className="ml-1 font-normal text-ink-3">
                  {i18n._(msg`override opzionale`)}
                </span>
              </label>
              <input
                id="member-cost-cap"
                type="number"
                min={0}
                step="0.01"
                inputMode="decimal"
                value={costCap}
                onChange={(e) => setCostCap(e.target.value)}
                placeholder={i18n._(msg`es. 25`)}
                aria-label={i18n._(msg`Cost cap override`)}
                className="h-9 w-full rounded-md border border-hairline bg-card px-2 text-[12.5px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="member-storage-gb"
                className="block font-medium text-[12px] text-ink-2"
              >
                {i18n._(msg`Storage limit (GB)`)}
                <span className="ml-1 font-normal text-ink-3">
                  {i18n._(msg`override opzionale`)}
                </span>
              </label>
              <input
                id="member-storage-gb"
                type="number"
                min={0}
                step="0.1"
                inputMode="decimal"
                value={storageGb}
                onChange={(e) => setStorageGb(e.target.value)}
                placeholder={i18n._(msg`es. 50`)}
                aria-label={i18n._(msg`Storage limit override`)}
                className="h-9 w-full rounded-md border border-hairline bg-card px-2 text-[12.5px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              />
              <p className="text-[11px] text-ink-3">
                {i18n._(
                  msg`Lascia vuoto per ereditare dal tier. I valori sono salvati in byte sul backend.`,
                )}
              </p>
            </div>
          </TabsContent>

          <TabsContent value="connectors" className="space-y-2 pt-2">
            {connectorsQuery.isLoading ? (
              <div
                className="flex justify-center py-6"
                role="status"
                aria-label={i18n._(msg`Caricamento connector`)}
              >
                <Loader2 size={18} className="animate-spin text-ink-3" aria-hidden />
              </div>
            ) : connectorsQuery.isError ? (
              <p className="py-3 text-center text-[12px] text-destructive">
                {i18n._(msg`Errore nel caricamento dei connector.`)}
              </p>
            ) : connectors.length === 0 ? (
              <p className="py-3 text-center text-[12px] text-ink-3">
                {i18n._(msg`Nessun connector abilitato per l'org.`)}
              </p>
            ) : (
              <ul
                className="max-h-[260px] space-y-1.5 overflow-y-auto"
                aria-label={i18n._(msg`Accesso connector per membro`)}
              >
                {connectors.map((mc) => {
                  const busy = pendingConnector === mc.connector_key;
                  return (
                    <li
                      key={mc.connector_key}
                      className={cn(
                        "flex items-center justify-between rounded-md border border-hairline px-3 py-2",
                        mc.is_assigned && "border-accent/30 bg-ando-orange-soft/30",
                      )}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <span className="truncate font-medium text-[12.5px] text-ink-1">
                          {mc.display_name}
                        </span>
                        {mc.category ? (
                          <Badge variant="outline" className="shrink-0 text-[10px]">
                            {mc.category}
                          </Badge>
                        ) : null}
                        {!mc.can_toggle ? (
                          <span className="shrink-0 text-[10px] text-ink-3">
                            {i18n._(msg`(tutti)`)}
                          </span>
                        ) : null}
                      </div>
                      <Switch
                        checked={mc.is_assigned}
                        disabled={!mc.can_toggle || busy}
                        onCheckedChange={(next) => handleToggleConnector(mc.connector_key, next)}
                        aria-label={
                          mc.can_toggle
                            ? i18n._(msg`Accesso a ${mc.display_name}`)
                            : i18n._(msg`${mc.display_name} (esteso a tutti i membri)`)
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="text-[11px] text-ink-3">
              {i18n._(
                msg`I connector con licenza “tutti i membri” non sono toggleabili singolarmente.`,
              )}
            </p>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={savingLimits}
          >
            {i18n._(msg`Annulla`)}
          </Button>
          {activeTab === "limits" && <Button type="button" onClick={handleSaveLimits} disabled={savingLimits || !member}>
            {savingLimits ? (
              <Loader2 size={13} className="mr-1.5 animate-spin" aria-hidden />
            ) : null}
            {i18n._(msg`Salva limiti`)}
          </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
